import {useLazyQuery as useApolloLazyQuery} from '@apollo/client';
import {useMemoizedValue} from '@sail/react';
import {useCallback, useMemo} from 'react';
import useGetApolloContext from 'src/internal/apollo/useGetApolloContext';
import {
  DEFAULT_IGNORE_TRACKING_FIELDS,
  DEFAULT_TRACK_ACCESSES_SAMPLE_RATE,
  useTrackGraphQlFieldAccesses,
} from 'src/internal/deadwood/deadwood';

import type {
  LazyQueryPromiseReturn,
  UseLazyQueryOptions,
  UseLazyQueryReturn,
} from 'src/internal/apollo/types';
import type {
  GraphQlDocument,
  VariablesOfDoc,
} from 'src/internal/shared/graphql/types/document';

/**
 * @see https://sail.stripe.me/data/api/useLazyQuery
 */
export default function useLazyQuery<
  /** The data returned by the GraphQL query (inferred from the passed GraphQL document). */
  TData,
  /** The variables that the GraphQL query accept (inferred from the passed GraphQL document). */
  TVariables,
  /** The permissions needed by the GraphQL query (inferred from the passed GraphQL document). */
  TPermissions,
  /** (internal) The variables passed to the lazy query via the hook (inferred from the passed options). */
  THookVariables extends Partial<TVariables> | undefined = undefined,
  TokenIsOptional extends boolean = false,
>(
  /**
   * The GraphQL query that is going to be executed. This must be created with the
   * [`gql`](/apis/data/gql) or
   * [`graphql`](/apis/data/graphql) template literal.
   */
  query: GraphQlDocument<TData, TVariables, TPermissions>,
  /**
   * Optional options to pass to the query.
   * Refer to [operation options](/data/guides/graphql-usage/the-basics-runtime#operation-options) docs.
   */
  options: UseLazyQueryOptions<
    GraphQlDocument<TData, TVariables, TPermissions>,
    THookVariables
  > = {},
): /**
 * Array with two elements:
 *
 * **callback**: Function to execute the actual query. Accepts an options object that will override any options passed to the hook (including the variables and the permissions token).
 *
 * **result**: Object with `data`, `loading` and `error` (similar to the one that the
 * [`useQuery` Apollo hook](https://www.apollographql.com/docs/react/api/react/hooks/#result) returns).
 */
UseLazyQueryReturn<
  GraphQlDocument<TData, TVariables, TPermissions>,
  THookVariables,
  TokenIsOptional
> {
  const baseContext = useGetApolloContext(options);

  const queryOptions = useMemo(
    () => ({
      ...options,
      context: baseContext,
    }),
    [baseContext, options],
  );

  const {hookVariables, hookOptionsWithoutVariables} = useMemo(() => {
    const {variables, ...rest} = queryOptions;

    return {
      hookVariables: variables,
      hookOptionsWithoutVariables: rest,
    };
  }, [queryOptions]);

  const memoizedHookVariables = useMemoizedValue(hookVariables);

  const [callback, result] = useApolloLazyQuery(
    query,
    hookOptionsWithoutVariables,
  );

  const callbackWrapped = useCallback(
    async (
      ...restArgs: Parameters<
        UseLazyQueryReturn<
          GraphQlDocument<TData, TVariables, TPermissions>,
          THookVariables,
          TokenIsOptional
        >[0]
      >
    ): Promise<
      LazyQueryPromiseReturn<
        GraphQlDocument<TData, TVariables, TPermissions>,
        THookVariables
      >
    > => {
      const cbOptions = restArgs[0];

      const mergedVariables = {
        ...memoizedHookVariables,
        ...cbOptions?.variables,
      } as VariablesOfDoc<GraphQlDocument<TData, TVariables, TPermissions>>;

      const mergedOptions = {
        ...cbOptions,
        variables: mergedVariables,
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {refetch, ...result} = await callback(mergedOptions);

      return result;
    },
    [callback, memoizedHookVariables],
  );

  return [
    callbackWrapped,

    useTrackGraphQlFieldAccesses(result, {
      query,
      sampleRate: options.trackAccesses ?? DEFAULT_TRACK_ACCESSES_SAMPLE_RATE,
      ignoreFields: DEFAULT_IGNORE_TRACKING_FIELDS,
    }),
  ];
}
